import React, { useState, useEffect } from 'react';
import { Plus, Edit, UserX, UserCheck, Users, AlertCircle, Loader, ShoppingCart, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { chatbotService } from '../../../services/chatbotService';
import { useDateFormat } from '../../../hooks/useDateFormat';

const ROLES = ['AGENT_SUPPORT', 'SUPERVISOR'];
const SLOT_OPTIONS = [1, 3, 5, 10];

const AgentManagement = () => {
  const { t } = useTranslation();
  const { formatDateTime } = useDateFormat();

  const [agents, setAgents] = useState([]);
  const [agentUsage, setAgentUsage] = useState(null); // { includedInPlan, extraSlots, maxAgents }
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Create / edit modal
  const [showModal, setShowModal] = useState(false);
  const [editingAgent, setEditingAgent] = useState(null);
  const [formData, setFormData] = useState({ fullName: '', email: '', password: '', role: 'AGENT_SUPPORT' });
  const [formError, setFormError] = useState(null);
  const [saving, setSaving] = useState(false);

  // Deactivate confirm
  const [confirmDeactivate, setConfirmDeactivate] = useState(null);
  const [deactivating, setDeactivating] = useState(false);

  // Activate confirm
  const [confirmActivate, setConfirmActivate] = useState(null);
  const [activating, setActivating] = useState(false);

  // Purchase slots modal
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [purchaseQty, setPurchaseQty] = useState(1);
  const [purchasing, setPurchasing] = useState(false);
  const [purchaseError, setPurchaseError] = useState(null);

  const activeAgentCount = agents.filter(a => a.isActive).length;
  const maxAgents = agentUsage?.maxAgents ?? null;
  const atLimit = maxAgents !== null && activeAgentCount >= maxAgents;

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [data, usage] = await Promise.all([
        chatbotService.getAgents(),
        chatbotService.getAgentUsage().catch(() => null),
      ]);
      setAgents(Array.isArray(data) ? data : []);
      setAgentUsage(usage);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  const openCreate = () => {
    setEditingAgent(null);
    setFormData({ fullName: '', email: '', password: '', role: 'AGENT_SUPPORT' });
    setFormError(null);
    setShowModal(true);
  };

  const openEdit = (agent) => {
    setEditingAgent(agent);
    setFormData({ fullName: agent.fullName, email: agent.email, password: '', role: agent.role });
    setFormError(null);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingAgent(null);
    setFormError(null);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setFormError(null);
    try {
      if (editingAgent) {
        const payload = { fullName: formData.fullName, email: formData.email, role: formData.role };
        if (formData.password) payload.password = formData.password;
        await chatbotService.updateAgent(editingAgent.id, payload);
      } else {
        await chatbotService.createAgent(formData);
      }
      closeModal();
      await loadData();
    } catch (err) {
      setFormError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async () => {
    if (!confirmDeactivate) return;
    setDeactivating(true);
    try {
      await chatbotService.deactivateAgent(confirmDeactivate.id);
      setConfirmDeactivate(null);
      await loadData();
    } catch (err) {
      setError(err.message);
      setConfirmDeactivate(null);
    } finally {
      setDeactivating(false);
    }
  };

  const handleActivate = async () => {
    if (!confirmActivate) return;
    setActivating(true);
    try {
      await chatbotService.activateAgent(confirmActivate.id);
      setConfirmActivate(null);
      await loadData();
    } catch (err) {
      setError(err.message);
      setConfirmActivate(null);
    } finally {
      setActivating(false);
    }
  };

  const handlePurchaseSlots = async () => {
    setPurchasing(true);
    setPurchaseError(null);
    try {
      const updated = await chatbotService.purchaseAgentSlots(purchaseQty);
      setAgentUsage(updated);
      setShowPurchaseModal(false);
      setPurchaseQty(1);
    } catch (err) {
      setPurchaseError(err.message);
    } finally {
      setPurchasing(false);
    }
  };

  const roleLabel = (role) =>
    role === 'SUPERVISOR' ? t('agents.roleSupervisor') : t('agents.roleSupport');

  const initials = (name) =>
    name ? name.split(' ').map(p => p[0]).join('').toUpperCase().slice(0, 2) : '?';

  const usagePercent = maxAgents > 0 ? Math.min(100, Math.round((activeAgentCount / maxAgents) * 100)) : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader className="w-6 h-6 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('agents.title')}</h1>
          <p className="text-sm text-gray-500 mt-1">{t('agents.subtitle')}</p>
        </div>
        <div className="flex items-center gap-2">
          {atLimit && (
            <button
              onClick={() => { setPurchaseError(null); setPurchaseQty(1); setShowPurchaseModal(true); }}
              className="flex items-center gap-2 px-4 py-2 border border-primary-600 text-primary-600 rounded-lg hover:bg-primary-50 transition-colors text-sm font-medium"
            >
              <ShoppingCart className="w-4 h-4" />
              {t('agents.purchaseSlots')}
            </button>
          )}
          <button
            onClick={openCreate}
            disabled={atLimit}
            title={atLimit ? t('agents.limitReached') : undefined}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Plus className="w-4 h-4" />
            {t('agents.addAgent')}
          </button>
        </div>
      </div>

      {/* Agent usage bar */}
      {agentUsage !== null && maxAgents !== null && (
        <div className="bg-white rounded-xl border border-gray-200 px-5 py-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">{t('agents.agentSeats')}</span>
            <span className={`text-sm font-semibold ${atLimit ? 'text-red-600' : 'text-gray-900'}`}>
              {t('agents.usageOf', { used: activeAgentCount, max: maxAgents })}
            </span>
          </div>
          <div className="w-full bg-gray-100 rounded-full h-2">
            <div
              className={`h-2 rounded-full transition-all ${atLimit ? 'bg-red-500' : usagePercent >= 80 ? 'bg-orange-500' : 'bg-primary-500'}`}
              style={{ width: `${usagePercent}%` }}
            />
          </div>
          <div className="flex items-center justify-between mt-1.5 text-xs text-gray-400">
            <span>{t('agents.includedInPlan', { count: agentUsage.includedInPlan })}</span>
            {agentUsage.extraSlots > 0 && (
              <span>{t('agents.extraSlots', { count: agentUsage.extraSlots })}</span>
            )}
            {atLimit && (
              <span className="text-red-500 font-medium">{t('agents.limitReached')}</span>
            )}
          </div>
        </div>
      )}

      {/* Error banner */}
      {error && (
        <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {error}
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {agents.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-gray-400">
            <Users className="w-12 h-12 mb-3 opacity-30" />
            <p className="text-sm font-medium">{t('agents.noAgents')}</p>
            <p className="text-xs mt-1">{t('agents.addFirst')}</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wide">{t('agents.name')}</th>
                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wide">{t('agents.email')}</th>
                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wide">{t('agents.role')}</th>
                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wide">{t('agents.status')}</th>
                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wide">{t('agents.lastSeen')}</th>
                <th className="px-6 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {agents.map((agent) => (
                <tr key={agent.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-primary-100 text-primary-700 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">
                        {initials(agent.fullName)}
                      </div>
                      <span className="font-medium text-gray-900">{agent.fullName}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-gray-600">{agent.email}</td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      agent.role === 'SUPERVISOR'
                        ? 'bg-purple-100 text-purple-700'
                        : 'bg-blue-100 text-blue-700'
                    }`}>
                      {roleLabel(agent.role)}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      agent.isActive
                        ? 'bg-green-100 text-green-700'
                        : 'bg-gray-100 text-gray-500'
                    }`}>
                      {agent.isActive ? t('agents.active') : t('agents.inactive')}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-gray-500 text-xs">
                    {agent.lastSeenAt
                      ? formatDateTime(agent.lastSeenAt)
                      : '—'}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => openEdit(agent)}
                        className="p-1.5 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                        title={t('common.edit')}
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      {agent.isActive ? (
                        <button
                          onClick={() => setConfirmDeactivate(agent)}
                          className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title={t('agents.deactivate')}
                        >
                          <UserX className="w-4 h-4" />
                        </button>
                      ) : (
                        <button
                          onClick={() => setConfirmActivate(agent)}
                          className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                          title={t('agents.activate')}
                        >
                          <UserCheck className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Create / Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              {editingAgent ? t('agents.editAgent') : t('agents.addAgent')}
            </h2>

            {formError && (
              <div className="mb-4 flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                {formError}
              </div>
            )}

            <form onSubmit={handleSave} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('agents.name')} *</label>
                <input
                  type="text"
                  value={formData.fullName}
                  onChange={e => setFormData(p => ({ ...p, fullName: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('agents.email')} *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={e => setFormData(p => ({ ...p, email: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  required
                  disabled={!!editingAgent}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('agents.password')} {editingAgent ? `(${t('common.optional')})` : '*'}
                </label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={e => setFormData(p => ({ ...p, password: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  required={!editingAgent}
                  minLength={8}
                  placeholder={editingAgent ? t('agents.passwordPlaceholder') : ''}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('agents.role')}</label>
                <select
                  value={formData.role}
                  onChange={e => setFormData(p => ({ ...p, role: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  {ROLES.map(r => (
                    <option key={r} value={r}>{roleLabel(r)}</option>
                  ))}
                </select>
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={closeModal}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm font-medium disabled:opacity-50"
                >
                  {saving ? t('common.loading') : t('common.save')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Purchase Slots Modal */}
      {showPurchaseModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">{t('agents.purchaseSlots')}</h2>
              <button onClick={() => setShowPurchaseModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            {agentUsage && (
              <p className="text-sm text-gray-500 mb-5">
                {t('agents.currentUsage', { used: activeAgentCount, max: maxAgents })}
              </p>
            )}

            {purchaseError && (
              <div className="mb-4 flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                {purchaseError}
              </div>
            )}

            <div className="mb-5">
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('agents.slotsToAdd')}</label>
              <div className="grid grid-cols-4 gap-2">
                {SLOT_OPTIONS.map(n => (
                  <button
                    key={n}
                    onClick={() => setPurchaseQty(n)}
                    className={`py-2 rounded-lg text-sm font-semibold border transition-colors ${
                      purchaseQty === n
                        ? 'bg-primary-600 text-white border-primary-600'
                        : 'bg-white text-gray-700 border-gray-300 hover:border-primary-400'
                    }`}
                  >
                    +{n}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowPurchaseModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handlePurchaseSlots}
                disabled={purchasing}
                className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm font-medium disabled:opacity-50"
              >
                {purchasing ? t('common.loading') : t('agents.addSlots', { count: purchaseQty })}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Activate Confirm Modal */}
      {confirmActivate && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6">
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <UserCheck className="w-6 h-6 text-green-600" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900 mb-2">{t('agents.activate')}</h2>
            <p className="text-sm text-gray-500 mb-6">{t('agents.activateConfirm')}</p>
            <div className="flex gap-3">
              <button
                onClick={() => setConfirmActivate(null)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleActivate}
                disabled={activating}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium disabled:opacity-50"
              >
                {activating ? t('common.loading') : t('agents.activate')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Deactivate Confirm Modal */}
      {confirmDeactivate && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mb-4">
              <UserX className="w-6 h-6 text-red-600" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900 mb-2">{t('agents.deactivate')}</h2>
            <p className="text-sm text-gray-500 mb-6">{t('agents.deactivateConfirm')}</p>
            <div className="flex gap-3">
              <button
                onClick={() => setConfirmDeactivate(null)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleDeactivate}
                disabled={deactivating}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium disabled:opacity-50"
              >
                {deactivating ? t('common.loading') : t('agents.deactivate')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AgentManagement;
